// Session Manager for STM
// Generates and manages unique session IDs for tracking user sessions
import { getAuthUserEmail, getAuthUserId } from '../services/auth';

const SESSION_ID_KEY = 'stm_session_id';
const SESSION_TIMESTAMP_KEY = 'stm_session_timestamp';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Generate a unique session ID
 * Format: timestamp-randomId
 */
function generateSessionId() {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomId}`;
}

/**
 * Get the current session ID or create a new one if expired
 */
export function getSessionId() {
  const storedId = sessionStorage.getItem(SESSION_ID_KEY);
  const storedTimestamp = sessionStorage.getItem(SESSION_TIMESTAMP_KEY);
  
  const now = Date.now();
  
  // Check if we have a valid session
  if (storedId && storedTimestamp) {
    const sessionAge = now - parseInt(storedTimestamp);
    
    // If session is still valid (less than 30 minutes old), update timestamp and return it
    if (sessionAge < SESSION_DURATION) {
      sessionStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());
      return storedId;
    }
  }
  
  // Create new session
  const newSessionId = generateSessionId();
  sessionStorage.setItem(SESSION_ID_KEY, newSessionId);
  sessionStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());
  
  return newSessionId;
}

/**
 * Get session info including session ID and user ID from GA cookies
 */
export function getSessionInfo() {
  const sessionId = getSessionId();
  
  // Try to extract GA client ID from cookies
  let userId = getAuthUserId();
  const userEmail = getAuthUserEmail();
  const cookies = document.cookie;
  
  // Parse GA client ID (_ga cookie)
  if (!userId && cookies.includes('_ga=')) {
    try {
      // Extract _ga cookie value
      const gaValue = cookies.split('_ga=')[1].split(';')[0];
      // GA cookie format: GA{version}.{parts}.{client_id_part1}.{client_id_part2}
      const gaParts = gaValue.split('.');
      if (gaParts.length >= 4) {
        userId = `${gaParts[2]}.${gaParts[3]}`;
      }
    } catch (e) {
      console.error('Error parsing GA cookie:', e);
    }
  }
  
  return {
    sessionId,
    userId,
    userEmail,
    cookies // Return full cookie string for API logger
  };
}

/**
 * Create headers object with session tracking info
 */
export function createSessionHeaders() {
  const sessionInfo = getSessionInfo();

  const headers = {
    'X-STM-Session-ID': sessionInfo.sessionId,
    'X-STM-User-ID': sessionInfo.userId || 'anonymous',
    'X-GA-Cookies': sessionInfo.cookies // Keep this for backward compatibility
  };

  if (sessionInfo.userEmail) {
    headers['X-STM-User-Email'] = sessionInfo.userEmail;
  }

  return headers;
}
